import { createSelector } from 'reselect';
import { AggregateInAppToBreakdown, MediumInAppToBreakdown } from './constants';

export const selectCampaigns = (state) => state.messagingInsights.campaigns;
export const selectCampaignsLoading = (state) => state.messagingInsights.campaignsLoading;
export const selectCampaignsFilters = (state) => state.messagingInsights.campaignsFilter;
export const selectReportData = (state) => state.messagingInsights.reportDataAsCsv;
export const selectOverallPerformanceData = (state) =>
  state.messagingInsights.overallPerformanceData;
export const selectSegmentData = (state) => state.messagingInsights.segmentData;
export const selectAggStatsLoading = (state) => state.messagingInsights.aggStatsLoading;
export const selectAggStatsFilters = (state) => state.messagingInsights.aggStatsFilter;
export const selectSegmentsLoading = (state) => state.messagingInsights.segmentsLoading;
export const selectInsightsFiltersLoading = (state) => state.messagingInsights.filtersLoading;
export const selectInsightsFilters = (state) => state.messagingInsights.filters;
export const selectInsightsFiltersLoaded = (state) => state.messagingInsights.filtersLoaded;
export const selectTimeSeriesData = (state) => state.messagingInsights.timeSeriesData;
export const selectDeviceData = (state) => state.messagingInsights.deviceData;
export const selectLocationData = (state) => state.messagingInsights.locationData;

export const selectPreviewThumbnails = (state) => state.messagingInsights.previewThumbnails;
export const selectIsCsvExportInProgress = (state) => state.messagingInsights.csvExportInProgress;
export const selectSortedCampaignIdsByMetric = (state) => state.messagingInsights.sortedCampaignIds;
export const selectSortedCampaignIdsByMetricLoading = (state) =>
  state.messagingInsights.sortedCampaignIdsLoading;
export const selectAppliedCrossMetrics = (state) => state.messagingInsights.appliedCrossMetrics;
export const selectAppliedMediums = (state) => state.messagingInsights.appliedMediums;

export const selectProjectId = (state) => state.userContext.project?.id;

export const selectAllMetricsForCampaigns = (state) =>
  state.messagingInsights.allMetricsForCampaigns;
export const selectAllMetricsForCampaignsLoading = (state) =>
  state.messagingInsights.allMetricsForCampaignsLoading;

export const selectError = (state) => state.messagingInsights.error;

export const selectSavedView = (state) => state.messagingInsights.savedView;
export const selectSavedViewLoading = (state) => state.messagingInsights.savedViewLoading;
export const selectSavedViewLoaded = (state) => state.messagingInsights.savedViewLoaded;

export const selectUpdateViewLoading = (state) => state.messagingInsights.updateViewLoading;
export const selectCreateViewLoading = (state) => state.messagingInsights.createViewLoading;
export const selectUpdateViewStatus = (state) => state.messagingInsights.updateViewStatus;
export const selectSavedViews = (state) => state.messagingInsights.savedViews;
export const selectSavedViewsPaginationOpts = (state) =>
  state.messagingInsights.savedViewsPaginationOpts;
export const selectProjectDefaultView = (state) => state.messagingInsights.projectDefaultView;
export const selectSavedViewsLoading = (state) => state.messagingInsights.savedViewsLoading;
export const selectRecentSavedViews = (state) => state.messagingInsights.recentSavedViews;
export const selectRecentSavedViewsLoading = (state) =>
  state.messagingInsights.recentSavedViewsLoading;
export const selectPDFReportUrl = (state) => state.messagingInsights.pdfReportUrl;
export const selectPDFReportLoading = (state) => state.messagingInsights.pdfReportLoading;

export const selectAllMetricsPerCampaign = createSelector(
  [selectAllMetricsForCampaigns],
  (allMetrics) => {
    return allMetrics?.reduce((acc, metrics) => {
      acc[metrics.campaignId] = {
        ...metrics.mediumMetrics,
        ...metrics.overallMetrics,
      };
      if (Object.keys(metrics.breakdownMetrics || {}).length) {
        acc[metrics.campaignId] = {
          ...acc[metrics.campaignId],
          breakdownMetrics: metrics.breakdownMetrics,
        };
      }
      return acc;
    }, {});
  },
);

export const selectCampaignMetricsTableData = createSelector(
  [selectCampaigns, selectAllMetricsPerCampaign, selectSortedCampaignIdsByMetric],
  (campaigns, allMetricsPerCampaign, sortedCampaignIds) => {
    const metricsPerCampaign = allMetricsPerCampaign;

    if (sortedCampaignIds?.length) {
      const sortedTableData = [];
      sortedCampaignIds.forEach((id) => {
        const campaignData = campaigns.find((campaign) => campaign.campaignId === id);
        if (campaignData) {
          sortedTableData.push({
            ...campaignData,
            metrics: metricsPerCampaign?.[id] || {},
          });
        }
      });
      return sortedTableData;
    } else {
      return campaigns?.map((campaign) => ({
        ...campaign,
        metrics: metricsPerCampaign?.[campaign.campaignId] || {},
      }));
    }
  },
);

export const selectCampaignIds = createSelector([selectCampaignMetricsTableData], (campaigns) => {
  if (campaigns?.length) {
    return campaigns?.map((campaign) => parseInt(campaign.campaignId, 10));
  }
  return [];
});

// Campaign ids fetched from the new performance endpoint, in order to be able fetch the metrics
// whereas `selectCampaignIds` is for selecting the campaigns ids that already has the table data fetched
export const selectCampaignIdsToFetchMetrics = createSelector([selectCampaigns], (campaigns) => {
  if (campaigns?.length) {
    return campaigns?.map((campaign) => parseInt(campaign.campaignId, 10));
  }
  return [];
});

export const selectTemplateIds = createSelector(
  [selectCampaignMetricsTableData],
  (campaignData = []) => {
    const selectedIds = [];
    campaignData?.forEach((campaign) => {
      selectedIds.push(parseInt(campaign.templateId, 10));
    });
    return selectedIds;
  },
);

export const selectMediumDestinationsByBreakdownMetrics = createSelector(
  [selectOverallPerformanceData],
  (aggregateData) => {
    const destinationsObj = {};
    Object.keys(aggregateData?.mediumMetrics || {}).forEach((metric) => {
      if (MediumInAppToBreakdown[metric]) {
        const destinations =
          aggregateData?.mediumMetrics?.[MediumInAppToBreakdown[metric]]?.value?.destinations;
        if (destinations?.length) {
          const destinationsWithoutAppName = destinations.filter(
            (destination) => !destination.appName,
          );
          const othersAggValue = destinationsWithoutAppName.reduce(
            (totalVal, dest) => totalVal + parseFloat(dest.value),
            0,
          );
          if (destinationsWithoutAppName.length) {
            const filteredDestinations = destinations.filter((destination) => destination.appName);
            if (metric.toLowerCase().includes('rate')) {
              const othersArr = destinationsWithoutAppName.map((dest) => ({
                appName: `Others (${dest.appPackageName})`,
                platform: '',
                appPackageName: '',
                value: dest.value,
              }));
              destinationsObj[metric] = [...filteredDestinations, ...othersArr];
            } else {
              destinationsObj[metric] = [
                ...filteredDestinations,
                { appName: 'Others', platform: '', appPackageName: '', value: othersAggValue },
              ];
            }
          } else {
            destinationsObj[metric] = destinations;
          }
        }
      }
    });
    return destinationsObj;
  },
);

export const selectAggregateDestinationsByBreakdownMetrics = createSelector(
  [selectOverallPerformanceData],
  (aggregateData) => {
    const destinationsObj = {};
    Object.keys(aggregateData?.overallMetrics || {}).forEach((metric) => {
      if (AggregateInAppToBreakdown[metric]) {
        const destinations =
          aggregateData?.mediumMetrics?.[AggregateInAppToBreakdown[metric]]?.value?.destinations;
        if (destinations?.length) {
          const destinationsWithoutAppName = destinations.filter(
            (destination) => !destination.appName,
          );
          const othersAggValue = destinationsWithoutAppName.reduce(
            (totalVal, dest) => totalVal + parseFloat(dest.value),
            0,
          );
          if (destinationsWithoutAppName.length) {
            const filteredDestinations = destinations.filter((destination) => destination.appName);
            if (metric.toLowerCase().includes('rate')) {
              const othersArr = destinationsWithoutAppName.map((dest) => ({
                appName: `Others (${dest.appPackageName})`,
                platform: '',
                appPackageName: '',
                value: dest.value,
              }));
              destinationsObj[metric] = [...filteredDestinations, ...othersArr];
            } else {
              destinationsObj[metric] = [
                ...filteredDestinations,
                { appName: 'Others', platform: '', appPackageName: '', value: othersAggValue },
              ];
            }
          } else {
            destinationsObj[metric] = destinations;
          }
        }
      }
    });
    return destinationsObj;
  },
);
